export interface HostedFrameMessage {
	type: string
	height?: number
}

type HostedFrameOptions<TData extends HostedFrameMessage> = {
	allowedOrigin?: string
	height?: string
	onCloseButton?: () => void
	onMessage: (
		data: TData,
		container: HTMLDivElement,
		event: MessageEvent,
	) => void
	width?: string
}

type HostedFrameInstance = {
	container: HTMLDivElement
	iframe: HTMLIFrameElement
}

const SDK_SUPPORTED_LOCALES = [
	"en",
	"zh-CN",
	"zh-Hant",
	"fr",
	"de",
	"ja",
	"es",
	"ko",
	"nl",
	"it",
	"pt",
	"pt-BR",
] as const

const SDK_DEFAULT_LOCALE = "zh-CN"

const SDK_LOCALE_ALIASES: Record<string, string> = {
	en: "en",
	"en-us": "en",
	"en-gb": "en",
	zh: "zh-CN",
	"zh-cn": "zh-CN",
	"zh-tw": "zh-Hant",
	"zh-hk": "zh-Hant",
	"zh-hant": "zh-Hant",
	"zh-hans": "zh-CN",
	fr: "fr",
	de: "de",
	ja: "ja",
	es: "es",
	ko: "ko",
	nl: "nl",
	it: "it",
	pt: "pt",
	"pt-br": "pt-BR",
}

function matchSupportedBaseLocale(locale: string): string | undefined {
	const base = locale.toLowerCase().split("-")[0]
	return SDK_SUPPORTED_LOCALES.find((item) => item.toLowerCase() === base)
}

export function normalizeSdkLocale(locale?: string): string | undefined {
	if (!locale) return

	const sanitized = locale.trim().replace(/_/g, "-")
	if (!sanitized) return

	const lower = sanitized.toLowerCase()
	if (SDK_LOCALE_ALIASES[lower]) {
		return SDK_LOCALE_ALIASES[lower]
	}

	let canonical: string | undefined
	try {
		canonical = Intl.getCanonicalLocales(sanitized)[0]
	} catch {
		canonical = undefined
	}

	const candidates = [canonical, sanitized].filter(Boolean) as string[]

	for (const candidate of candidates) {
		const candidateLower = candidate.toLowerCase()

		if (SDK_LOCALE_ALIASES[candidateLower]) {
			return SDK_LOCALE_ALIASES[candidateLower]
		}
		if ((SDK_SUPPORTED_LOCALES as readonly string[]).includes(candidate)) {
			return candidate
		}

		const baseMatch = matchSupportedBaseLocale(candidate)
		if (baseMatch) {
			return baseMatch
		}
	}
}

export function getBrowserLocale(): string | undefined {
	if (typeof navigator === "undefined") return

	for (const candidate of navigator.languages || []) {
		const normalized = normalizeSdkLocale(candidate)
		if (normalized) return normalized
	}

	return normalizeSdkLocale(navigator.language)
}

export function getDocumentLocale(): string | undefined {
	if (typeof document === "undefined") return
	return normalizeSdkLocale(document.documentElement?.lang)
}

export function getAutoResolvedLocale(locale?: string): string {
	return (
		normalizeSdkLocale(locale) ||
		getDocumentLocale() ||
		getBrowserLocale() ||
		SDK_DEFAULT_LOCALE
	)
}

export function applyLocaleToUrl(urlValue: string, locale?: string): string {
	if (!locale) return urlValue

	try {
		const url = new URL(urlValue)
		const localePrefix = `/${locale}`
		if (
			!url.pathname.startsWith(`${localePrefix}/`) &&
			url.pathname !== localePrefix
		) {
			url.pathname = `${localePrefix}${url.pathname}`
		}
		return url.toString()
	} catch (_error) {
		const localePrefix = `/${locale}`
		if (!urlValue.startsWith(`${localePrefix}/`) && urlValue !== localePrefix) {
			return `${localePrefix}${urlValue.startsWith("/") ? "" : "/"}${urlValue}`
		}
		return urlValue
	}
}

export class HostedFrameModal<TData extends HostedFrameMessage> {
	protected iframe: HTMLIFrameElement | null = null
	protected modal: HTMLDivElement | null = null
	protected messageHandler: ((event: MessageEvent) => void) | null = null

	protected openHostedFrame(
		url: string,
		options: HostedFrameOptions<TData>,
	): HostedFrameInstance | null {
		if (typeof document === "undefined") return null
		if (this.modal) return null

		this.modal = document.createElement("div")
		Object.assign(this.modal.style, {
			position: "fixed",
			top: "0",
			left: "0",
			width: "100%",
			height: "100%",
			backgroundColor: "rgba(15,23,42,0.52)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: "9999",
			transition: "opacity 0.3s ease",
			backdropFilter: "blur(14px)",
			padding: "16px",
		})

		const container = document.createElement("div")
		Object.assign(container.style, {
			width: options.width || "450px",
			height: options.height || "min(680px, calc(100vh - 32px))",
			backgroundColor: "transparent",
			borderRadius: "28px",
			overflow: "visible",
			position: "relative",
			boxShadow: "none",
			maxWidth: "calc(100vw - 32px)",
			maxHeight: "calc(100vh - 32px)",
			transition: "none",
		})

		const closeBtn = document.createElement("button")
		closeBtn.innerHTML = "×"
		Object.assign(closeBtn.style, {
			position: "absolute",
			right: "-14px",
			top: "-14px",
			fontSize: "20px",
			width: "36px",
			height: "36px",
			borderRadius: "9999px",
			border: "1px solid rgba(255,255,255,0.5)",
			background: "rgba(255,255,255,0.9)",
			cursor: "pointer",
			color: "#475569",
			zIndex: "2",
			boxShadow: "0 10px 30px rgba(15,23,42,0.16)",
		})
		closeBtn.onclick = () => {
			this.close()
			options.onCloseButton?.()
		}

		this.iframe = document.createElement("iframe")
		this.iframe.src = url
		this.iframe.allow = "local-network-access"
		Object.assign(this.iframe.style, {
			width: "100%",
			height: "100%",
			border: "none",
			borderRadius: "28px",
			background: "transparent",
			display: "block",
		})

		container.appendChild(closeBtn)
		container.appendChild(this.iframe)
		this.modal.appendChild(container)
		document.body.appendChild(this.modal)

		this.messageHandler = (event: MessageEvent) => {
			if (options.allowedOrigin && options.allowedOrigin !== "*") {
				if (event.origin !== options.allowedOrigin) {
					return
				}
			}

			const data = event.data as TData
			if (!data || typeof data !== "object" || !data.type) {
				return
			}

			options.onMessage(data, container, event)
		}

		window.addEventListener("message", this.messageHandler)

		return {
			container,
			iframe: this.iframe,
		}
	}

	close() {
		if (typeof window === "undefined") return

		if (this.messageHandler) {
			window.removeEventListener("message", this.messageHandler)
			this.messageHandler = null
		}

		if (this.modal?.parentNode) {
			this.modal.parentNode.removeChild(this.modal)
		}

		this.modal = null
		this.iframe = null
	}
}
